import {type InfiniteData, useQueryClient} from '@github-ui/react-query'

import {projectsQueryKey} from '../../queries/projects'
import type {ProjectsResponse} from './types'

/**
 * Reads `showTemplatesIndex` from any cached projects response for this owner/repo,
 * regardless of state/sort/template variant. Used to keep the Projects/Templates toggle
 * stable across the cross-route remount when a user clicks Templates: the templates
 * route's own query has no data yet, but a sibling cache entry from the projects route
 * already knows whether the repo's owner is an organization.
 *
 * Returns `undefined` when no cached response exists yet (e.g. first paint of a deep
 * link), so callers can keep the toggle hidden until the server confirms support.
 */
export function useShowTemplatesIndex(owner: string, repo: string): boolean | undefined {
  const queryClient = useQueryClient()
  const cached = queryClient.getQueriesData<InfiniteData<ProjectsResponse>>({
    queryKey: projectsQueryKey(owner, repo),
  })
  for (const [, data] of cached) {
    const flag = data?.pages[0]?.showTemplatesIndex
    if (flag !== undefined) return flag
  }
  return undefined
}
